import { createContext, useContext } from "react";
import type { ChatMessage } from "../../lib/assistant-messages";
import type { AssistantPageContext } from "../../lib/assistant-links";
import {
  NO_ASSISTANT_CAPABILITIES,
  type AssistantCapabilities,
} from "../../lib/assistant-access";

export const OPEN_ASSISTANT_EVENT = "open-assistant";
export const ASSISTANT_CONVERSATION_STORAGE_KEY = "assistant:conversationId";

export interface OpenAssistantDetail {
  prompt?: string;
}

export function openAssistant(detail: OpenAssistantDetail = {}) {
  document.dispatchEvent(new CustomEvent<OpenAssistantDetail>(OPEN_ASSISTANT_EVENT, { detail }));
}

export type AssistantStatusState = "idle" | "loading" | "ready" | "error" | "forbidden";

export interface AssistantContextValue {
  available: boolean;
  capabilities: AssistantCapabilities;
  enabled: boolean;
  model: string;
  statusState: AssistantStatusState;
  refreshStatus: () => void;
  open: boolean;
  setOpen: (open: boolean) => void;
  toggle: () => void;
  messages: ChatMessage[];
  isStreaming: boolean;
  historyLoading: boolean;
  historyError: boolean;
  reloadHistory: () => void;
  conversationId: string | null;
  draft: string;
  setDraft: (draft: string) => void;
  draftVersion: number;
  send: (text: string, context?: AssistantPageContext) => void;
  stop: () => void;
  retry: () => void;
  newChat: () => void;
}

const noop = (): void => {};

export const AssistantContext = createContext<AssistantContextValue>({
  available: false,
  capabilities: NO_ASSISTANT_CAPABILITIES,
  enabled: false,
  model: "",
  statusState: "idle",
  refreshStatus: noop,
  open: false,
  setOpen: noop,
  toggle: noop,
  messages: [],
  isStreaming: false,
  historyLoading: false,
  historyError: false,
  reloadHistory: noop,
  conversationId: null,
  draft: "",
  setDraft: noop,
  draftVersion: 0,
  send: noop,
  stop: noop,
  retry: noop,
  newChat: noop,
});

export function useAssistant(): AssistantContextValue {
  return useContext(AssistantContext);
}
